import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChatSocketService } from '../../../../services/chat/chat-socket.service';
import { ChatStore } from '../../../../services/chat/chat-store.service';
import { CrudEmployeeService } from '../../../../gt2/content/crud/crud-employee/crud-employee.service';
import { FuseDirectivesModule } from '../../../../../@fuse/directives/directives';
import { ChatBodyPipe } from '../chat-body.pipe';
import { ChatEmojiPickerComponent } from '../emoji-picker/chat-emoji-picker.component';
import { GenericAlertComponent } from '../../../../gt2/components/generic-alert/generic-alert.component';
import { formatFileSize, isImageFile, validateChatFiles } from '../chat-upload.util';
import { ChatAttachment, ChatConversation, ChatMessage, ChatReaction } from '../models/chat.models';
import { locale as english } from '../i18n/en-CA';
import { locale as french } from '../i18n/fr-CA';

/**
 * Fil de messages (ASC) — infinite scroll vers le haut (restauration de la
 * position après prepend), auto-scroll bas seulement si déjà en bas,
 * séparateurs de date, messages system, replies, édition inline.
 */
@Component({
  selector: 'chat-view',
  templateUrl: './chat-view.component.html',
  styleUrls: ['./chat-view.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatMenuModule,
    CommonModule,
    FormsModule,
    MatInputModule,
    TranslateModule,
    MatIconModule,
    MatToolbarModule,
    MatTooltipModule,
    FuseDirectivesModule,
    ChatBodyPipe,
    ChatEmojiPickerComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ChatViewComponent implements AfterViewInit, OnDestroy {
  @ViewChild('chatContent')
  private chatContent?: ElementRef<HTMLElement>;

  @ViewChild('replyInput')
  private replyInput?: ElementRef<HTMLTextAreaElement>;

  public draft = '';
  public readonly editing = signal<ChatMessage | null>(null);
  public readonly replyingTo = signal<ChatMessage | null>(null);

  // ---- Upload (T6) ----
  public readonly stagedItems = signal<{ file: File; previewUrl: string | null }[]>([]);
  public readonly uploadError = signal<{ key: string; name?: string } | null>(null);
  public readonly dragActive = signal(false);
  public readonly lightboxUrl = signal<string | null>(null);
  public readonly quickReactions = ['👍', '❤️', '😂'];
  private uploadErrorTimer: ReturnType<typeof setTimeout> | null = null;

  // ---- Typing (T6) ----
  public readonly typingUsers = signal<Map<string, string>>(new Map()); // uuid → name
  private typingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private static readonly TYPING_EXPIRY_MS = 4000;

  private atBottom = true;
  private static readonly BOTTOM_TOLERANCE_PX = 80;
  private static readonly TOP_LOAD_THRESHOLD_PX = 150;
  private static readonly SIGNED_URL_STALE_MS = 50 * 60 * 1000; // TTL 60 min − marge

  public readonly pinnedPanelOpen = signal(false);

  constructor(
    public store: ChatStore,
    private socket: ChatSocketService,
    private crudEmployeeService: CrudEmployeeService,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {
    this.translate.setTranslation(english.lang, english.data, true);
    this.translate.setTranslation(french.lang, french.data, true);

    // Auto-scroll bas sur nouveau message SEULEMENT si l'utilisateur y est déjà.
    effect(() => {
      this.store.activeMessages(); // dépendance
      if (this.atBottom) {
        this.scheduleScrollToBottom();
      }
    });

    // Changement de conversation → repartir en bas + reset composer/typing.
    effect(() => {
      this.store.activeConversationId();
      this.editing.set(null);
      this.replyingTo.set(null);
      this.draft = '';
      this.atBottom = true;
      this.clearAllTyping();
      this.scheduleScrollToBottom();
    });

    // ---- Typing (whispers) ----
    this.socket.typing$.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event.conversation_uuid !== this.store.activeConversationId() || event.id === this.myUserUuid) {
        return;
      }
      this.setTyping(event.id, event.name);
    });
    this.socket.typingStopped$.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event.conversation_uuid === this.store.activeConversationId()) {
        this.clearTyping(event.id);
      }
    });
    // L'arrivée du message d'un user clear aussi son indicateur.
    this.socket.messageSent$.pipe(takeUntilDestroyed()).subscribe((message) => {
      if (message.sender?.uuid) {
        this.clearTyping(message.sender.uuid);
      }
    });
  }

  // ---- Typing ----

  private setTyping(uuid: string, name: string): void {
    this.typingUsers.update((map) => new Map(map).set(uuid, name));

    const existing = this.typingTimers.get(uuid);
    if (existing) {
      clearTimeout(existing);
    }
    this.typingTimers.set(
      uuid,
      setTimeout(() => this.clearTyping(uuid), ChatViewComponent.TYPING_EXPIRY_MS),
    );
  }

  private clearTyping(uuid: string): void {
    const timer = this.typingTimers.get(uuid);
    if (timer) {
      clearTimeout(timer);
      this.typingTimers.delete(uuid);
    }
    if (this.typingUsers().has(uuid)) {
      this.typingUsers.update((map) => {
        const next = new Map(map);
        next.delete(uuid);
        return next;
      });
    }
  }

  private clearAllTyping(): void {
    for (const timer of this.typingTimers.values()) {
      clearTimeout(timer);
    }
    this.typingTimers.clear();
    this.typingUsers.set(new Map());
  }

  public typingLine(): string {
    const names = [...this.typingUsers().values()];
    if (names.length === 0) {
      return '';
    }
    if (names.length === 1) {
      return this.translate.instant('CHAT.TYPING_ONE', { name: names[0] });
    }
    if (names.length === 2) {
      return this.translate.instant('CHAT.TYPING_TWO', { a: names[0], b: names[1] });
    }
    return this.translate.instant('CHAT.TYPING_MANY');
  }

  public onComposerInput(): void {
    this.autoGrow();
    this.updateMentionAutocomplete();
    const activeId = this.store.activeConversationId();
    const myUuid = this.myUserUuid;
    if (activeId && myUuid && this.draft.trim() !== '' && !this.editing()) {
      this.socket.whisperTyping(activeId, { id: myUuid, name: this.myName });
    }
  }

  // ---- Mentions @ (T7 — textarea, token brut @[uuid] visible en V1) ----

  public readonly mentionCandidates = signal<{ uuid: string; name: string; isAll: boolean }[]>([]);
  public readonly mentionIndex = signal(0);
  private mentionStart = -1; // index du '@' dans le draft

  /** Noms des participants pour le rendu des chips (uuid → nom). */
  public readonly mentionNames = computed<ReadonlyMap<string, string>>(() => {
    const names = new Map<string, string>();
    for (const participant of this.store.activeConversation()?.participants ?? []) {
      if (participant.user) {
        names.set(participant.user.uuid.toLowerCase(), participant.user.name);
      }
    }
    return names;
  });

  /** Bulle surlignée quand le message ME mentionne (@[moi] ou @[all]). */
  public mentionsMe(message: ChatMessage): boolean {
    if (message.deleted || this.isMine(message)) {
      return false;
    }
    const myUuid = this.myUserUuid;
    return message.mentions_all || (!!myUuid && message.mentions.includes(myUuid));
  }

  private updateMentionAutocomplete(): void {
    const el = this.replyInput?.nativeElement;
    const cursor = el?.selectionStart ?? this.draft.length;
    const beforeCursor = this.draft.slice(0, cursor);
    const match = /(^|\s)@([\p{L}\p{N} _-]{0,30})$/u.exec(beforeCursor);

    if (!match || this.editing()) {
      this.closeMentionAutocomplete();
      return;
    }

    this.mentionStart = cursor - match[2].length - 1;
    const query = match[2].toLowerCase().trim();

    const candidates: { uuid: string; name: string; isAll: boolean }[] = [];
    for (const participant of this.conversation?.participants ?? []) {
      const user = participant.user;
      if (!user || user.uuid === this.myUserUuid) {
        continue;
      }
      if (query === '' || user.name.toLowerCase().includes(query)) {
        candidates.push({ uuid: user.uuid, name: user.name, isAll: false });
      }
    }
    // @all : owner/admin seulement (garde re-validée serveur — 422 sinon).
    if (this.canManage() && (query === '' || 'all'.startsWith(query))) {
      candidates.push({ uuid: 'all', name: '@all', isAll: true });
    }

    this.mentionCandidates.set(candidates.slice(0, 8));
    this.mentionIndex.set(0);
  }

  public selectMention(candidate: { uuid: string; name: string; isAll: boolean }): void {
    const el = this.replyInput?.nativeElement;
    const cursor = el?.selectionStart ?? this.draft.length;
    if (this.mentionStart < 0) {
      return;
    }

    const token = `@[${candidate.uuid}] `;
    this.draft = this.draft.slice(0, this.mentionStart) + token + this.draft.slice(cursor);
    const newCursor = this.mentionStart + token.length;
    this.closeMentionAutocomplete();

    queueMicrotask(() => {
      el?.focus();
      el?.setSelectionRange(newCursor, newCursor);
      this.autoGrow();
    });
  }

  public closeMentionAutocomplete(): void {
    this.mentionCandidates.set([]);
    this.mentionIndex.set(0);
    this.mentionStart = -1;
  }

  private get myName(): string {
    const me: any = this.crudEmployeeService.me;
    return me?.full_name ?? `${me?.first_name ?? ''} ${me?.last_name ?? ''}`.trim();
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
    this.replyInput?.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.clearStaging();
    this.clearAllTyping();
    if (this.uploadErrorTimer) {
      clearTimeout(this.uploadErrorTimer);
    }
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.lightboxUrl()) {
      this.closeLightbox();
    }
  }

  // ---- Accès store / identités ----

  public get conversation(): ChatConversation | null {
    return this.store.activeConversation();
  }

  public get messages(): ChatMessage[] {
    return this.store.activeMessages();
  }

  public get myUserUuid(): string | null {
    return this.crudEmployeeService.me?.user?.uuid ?? null;
  }

  public isMine(message: ChatMessage): boolean {
    return message.sender?.uuid === this.myUserUuid;
  }

  public canManage(): boolean {
    const role = this.conversation?.my_role;
    return role === 'owner' || role === 'admin';
  }

  public displayName(conversation: ChatConversation): string {
    return conversation.display_name ?? conversation.name ?? '';
  }

  public headerAvatar(conversation: ChatConversation): string | null {
    return conversation.display_avatar ?? conversation.avatar_url ?? null;
  }

  // ---- Groupement / séparateurs ----

  public isFirstMessageOfGroup(message: ChatMessage, i: number): boolean {
    const previous = this.messages[i - 1];
    return (
      i === 0 ||
      !previous ||
      previous.type === 'system' ||
      message.type === 'system' ||
      previous.sender?.uuid !== message.sender?.uuid ||
      this.showDateSeparator(i)
    );
  }

  public isLastMessageOfGroup(message: ChatMessage, i: number): boolean {
    const next = this.messages[i + 1];
    return (
      i === this.messages.length - 1 ||
      !next ||
      next.type === 'system' ||
      message.type === 'system' ||
      next.sender?.uuid !== message.sender?.uuid
    );
  }

  public shouldShowContactAvatar(message: ChatMessage, i: number): boolean {
    return !this.isMine(message) && message.type !== 'system' && this.isLastMessageOfGroup(message, i);
  }

  public showDateSeparator(i: number): boolean {
    const current = this.messages[i]?.created_at;
    if (!current) {
      return false;
    }
    if (i === 0) {
      return true;
    }
    const previous = this.messages[i - 1]?.created_at;
    return !previous || current.slice(0, 10) !== previous.slice(0, 10);
  }

  public systemLabel(message: ChatMessage): string {
    const action = message.metadata?.action ?? '';
    return this.translate.instant(`CHAT.SYSTEM.${action}`);
  }

  public trackByUuid(_index: number, message: ChatMessage): string {
    return message.uuid;
  }

  // ---- Scroll ----

  public onScroll(): void {
    const el = this.chatContent?.nativeElement;
    if (!el) {
      return;
    }

    this.atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < ChatViewComponent.BOTTOM_TOLERANCE_PX;
    this.store.setViewerAtBottom(this.atBottom); // mark-read intelligent (T6)

    const activeId = this.store.activeConversationId();
    if (
      activeId &&
      el.scrollTop < ChatViewComponent.TOP_LOAD_THRESHOLD_PX &&
      this.store.hasMore(activeId) &&
      !this.store.loadingOlder()
    ) {
      const previousHeight = el.scrollHeight;
      const previousTop = el.scrollTop;
      this.store.loadOlder(activeId).then((added) => {
        if (added > 0) {
          queueMicrotask(() => {
            // Restauration de la position après prepend (pas de saut visuel).
            el.scrollTop = previousTop + (el.scrollHeight - previousHeight);
          });
        }
      });
    }
  }

  private scrollToBottom(): void {
    const el = this.chatContent?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }

  /**
   * Scroll bas APRÈS le rendu : un microtask part avant que le DOM soit
   * peint (scrollHeight encore ancien), d'où l'ouverture qui restait en
   * haut. Double passe (frame suivante + filet 100ms) pour couvrir le
   * premier rendu de la liste.
   */
  private scheduleScrollToBottom(): void {
    requestAnimationFrame(() => this.scrollToBottom());
    setTimeout(() => this.scrollToBottom(), 100);
  }

  /** Les images chargent après le rendu et poussent le contenu — rester en bas. */
  public onAttachmentImageLoad(): void {
    if (this.atBottom) {
      this.scrollToBottom();
    }
  }

  public scrollToMessage(uuid: string): void {
    const target = document.getElementById(`chat-msg-${uuid}`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ---- Composer ----

  public onComposerKeydown(event: KeyboardEvent): void {
    // L'autocomplete @ capture la navigation clavier tant qu'elle est ouverte.
    const candidates = this.mentionCandidates();
    if (candidates.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.mentionIndex.update((i) => (i + delta + candidates.length) % candidates.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        this.selectMention(candidates[this.mentionIndex()]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMentionAutocomplete();
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
      return;
    }
    if (event.key === 'ArrowUp' && this.draft.trim() === '' && !this.editing()) {
      const lastMine = [...this.messages].reverse().find((m) => this.isMine(m) && m.type === 'text' && !m.deleted);
      if (lastMine) {
        event.preventDefault();
        this.startEdit(lastMine);
      }
      return;
    }
    if (event.key === 'Escape') {
      this.cancelEdit();
      this.replyingTo.set(null);
    }
  }

  public send(): void {
    const conversation = this.conversation;
    const body = this.draft.trim();
    const files = this.stagedItems().map((item) => item.file);
    if (!conversation || conversation.is_locked || (body === '' && files.length === 0)) {
      return;
    }

    const myUuid = this.myUserUuid;
    if (myUuid) {
      this.socket.whisperStopped(conversation.uuid, { id: myUuid }); // clear typing chez les autres
    }

    const editing = this.editing();
    if (editing) {
      if (body !== '') {
        this.store.editMessage(editing, body);
      }
      this.editing.set(null);
    } else if (files.length > 0) {
      this.store.sendMessageWithFiles(conversation.uuid, body || null, files, this.replyingTo()?.uuid ?? null);
      this.clearStaging();
      this.replyingTo.set(null);
      this.atBottom = true;
    } else {
      this.store.sendMessage(conversation.uuid, body, this.replyingTo()?.uuid ?? null);
      this.replyingTo.set(null);
      this.atBottom = true;
    }

    this.draft = '';
    this.autoGrow();
  }

  // ---- Emoji (T6) ----

  public insertEmoji(emoji: string): void {
    const el = this.replyInput?.nativeElement;
    if (!el) {
      this.draft += emoji;
      return;
    }
    const start = el.selectionStart ?? this.draft.length;
    const end = el.selectionEnd ?? start;
    this.draft = this.draft.slice(0, start) + emoji + this.draft.slice(end);
    queueMicrotask(() => {
      el.focus();
      const cursor = start + emoji.length;
      el.setSelectionRange(cursor, cursor);
      this.autoGrow();
    });
  }

  public toggleReaction(message: ChatMessage, emoji: string): void {
    this.store.toggleReaction(message, emoji);
  }

  /** Tooltip d'une pill : noms résolus depuis les participants chargés + « et N autres ». */
  public reactorsTooltip(reaction: ChatReaction): string {
    const participants = this.conversation?.participants ?? [];
    const names = reaction.reactors
      .map((uuid) => participants.find((p) => p.user?.uuid === uuid)?.user?.name)
      .filter((name): name is string => !!name);

    const extra = reaction.count - names.length;
    if (names.length === 0) {
      return String(reaction.count);
    }
    return extra > 0
      ? `${names.join(', ')} ${this.translate.instant('CHAT.AND_N_OTHERS', { count: extra })}`
      : names.join(', ');
  }

  // ---- Upload (T6) ----

  public onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragActive.set(true);
  }

  public onDragLeave(): void {
    this.dragActive.set(false);
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive.set(false);
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  /** Collage presse-papier (screenshots) — très utilisé en ERP interne. */
  public onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.length > 0) {
      event.preventDefault();
      this.addFiles(files);
    }
  }

  private addFiles(files: File[]): void {
    if (files.length === 0 || this.conversation?.is_locked || this.editing()) {
      return;
    }

    const result = validateChatFiles(this.stagedItems().map((item) => item.file), files);
    if (result.error) {
      this.showUploadError(result.error, result.offender);
      return;
    }

    const additions = files.map((file) => ({
      file,
      previewUrl: isImageFile(file) ? URL.createObjectURL(file) : null,
    }));
    this.stagedItems.update((items) => [...items, ...additions]);
    this.replyInput?.nativeElement.focus();
  }

  public removeStaged(index: number): void {
    this.stagedItems.update((items) => {
      const removed = items[index];
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return items.filter((_, i) => i !== index);
    });
  }

  private clearStaging(): void {
    // Les objectURLs du staging sont distincts de ceux du message optimiste
    // (le store recrée les siens) — révoquer ceux-ci.
    for (const item of this.stagedItems()) {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    }
    this.stagedItems.set([]);
  }

  private showUploadError(error: 'too_many' | 'too_big' | 'bad_type', offender?: string): void {
    const keys = { too_many: 'CHAT.UPLOAD_TOO_MANY', too_big: 'CHAT.UPLOAD_TOO_BIG', bad_type: 'CHAT.UPLOAD_BAD_TYPE' };
    this.uploadError.set({ key: keys[error], name: offender });
    if (this.uploadErrorTimer) {
      clearTimeout(this.uploadErrorTimer);
    }
    this.uploadErrorTimer = setTimeout(() => this.uploadError.set(null), 5000);
  }

  public fileSize(bytes: number): string {
    return formatFileSize(bytes);
  }

  /** Ouvre un attachment — rafraîchit les URLs signées si le message est vieux (> 50 min). */
  public async openAttachment(message: ChatMessage, attachment: ChatAttachment, event: MouseEvent): Promise<void> {
    event.preventDefault();
    if (message.pending) {
      return;
    }

    let target = attachment;
    let host = message;
    if (Date.now() - (message.fetched_at ?? 0) > ChatViewComponent.SIGNED_URL_STALE_MS) {
      host = await this.store.refreshMessage(message.uuid);
      target = host.attachments.find((a) => a.uuid === attachment.uuid) ?? attachment;
    }

    if (attachment.thumb_url) {
      this.lightboxUrl.set(target.url);
    } else if (target.url) {
      window.open(target.url, '_blank', 'noopener');
    }
  }

  public closeLightbox(): void {
    this.lightboxUrl.set(null);
  }

  public startEdit(message: ChatMessage): void {
    this.editing.set(message);
    this.replyingTo.set(null);
    this.draft = message.body ?? '';
    queueMicrotask(() => {
      this.replyInput?.nativeElement.focus();
      this.autoGrow();
    });
  }

  public cancelEdit(): void {
    if (this.editing()) {
      this.editing.set(null);
      this.draft = '';
      this.autoGrow();
    }
  }

  public startReply(message: ChatMessage): void {
    this.replyingTo.set(message);
    this.editing.set(null);
    this.replyInput?.nativeElement.focus();
  }

  public autoGrow(): void {
    const el = this.replyInput?.nativeElement;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
    }
  }

  // ---- Actions message ----

  /** Confirmation avant suppression (pattern GenericAlertComponent maison). */
  public deleteMessage(message: ChatMessage): void {
    const confirmRef = this.dialog.open(GenericAlertComponent, { disableClose: false });
    confirmRef.componentInstance.useCancel = true;
    confirmRef.componentInstance.alertTitle = this.translate.instant('CHAT.DELETE');
    confirmRef.componentInstance.alertMessage = this.translate.instant('CHAT.DELETE_MESSAGE_CONFIRM');
    confirmRef.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.store.deleteMessage(message);
        }
      },
    });
  }

  public copyText(message: ChatMessage): void {
    if (message.body) {
      navigator.clipboard?.writeText(message.body);
    }
  }

  public togglePinnedPanel(): void {
    const open = !this.pinnedPanelOpen();
    this.pinnedPanelOpen.set(open);
    const activeId = this.store.activeConversationId();
    if (open && activeId) {
      this.store.loadPinned(activeId);
    }
  }

  public goToPinned(message: ChatMessage): void {
    this.pinnedPanelOpen.set(false);
    this.scrollToMessage(message.uuid);
  }

  public togglePin(message: ChatMessage): void {
    this.store.togglePin(message);
  }

  public retry(message: ChatMessage): void {
    if (message.client_id && this.conversation) {
      this.store.retryMessage(this.conversation.uuid, message.client_id);
    }
  }

  public toggleMute(): void {
    if (this.conversation) {
      this.store.toggleMute(this.conversation);
    }
  }
}
